import { type FC, forwardRef, type MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import {
  ChevronLeft,
  ChevronRight,
  FileDownIcon,
  SlidersHorizontal,
  Star,
  Trash2Icon,
  Video,
} from 'lucide-react';

import { getMediaOptions } from '@/api/media';
import { env } from '@/constants/env';
import { useDeleteFloorLayouts } from '@/features/estate-media/api/delete-floor-layouts';
import { useDeleteImage as useDeleteEstateImage } from '@/features/estate-media/api/delete-image';
import { ACCEPTED_MEDIA_TYPES } from '@/features/estate-media/schema/upload-media';
import { useDeleteFloorLayouts as useDeleteProjectFloorLayouts } from '@/features/project-media/api/delete-floor-layouts';
import { useDeleteImage as useDeleteProjectImage } from '@/features/project-media/api/delete-image';
import { useFullPath } from '@/hooks/use-full-path';
import { cn } from '@/lib/utils';
import { parseStringToBool } from '@/utils/parseStringToBool';

import { Button } from './button';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { useModal } from '../modals/new/use-modal';
import { ConfirmationModal } from '../modals/confirmation-modal';

type CarouselImage = {
  id?: string;
  name?: string;
  mediaId: string;
  estateId?: string;
  projectId?: string;
  sourceId?: string;
  createdAt?: string;
  updatedAt?: string;
  fileType?: string;
  isUsedByFlatfinder?: boolean;
};

type ImageEntry =
  | {
      id?: string | null;
      name?: string | null;
      createdAt?: string | null;
      mediaId?: string | null;
      estateId?: string | null;
      projectId?: string | null;
      updatedAt?: string | null;
      fileType?: string | null;
      isUsedByFlatfinder?: boolean;
    }
  | CarouselImage;

interface ImageCarouselProps {
  preselectedCurrentIndex?: number;
  className?: string;
  imageWrapperClassName?: string | ((active: boolean) => string);
  imageClassName?: string;
  selectedImageId?: string;
  imageWidth?: number;
  imageBeautificationButtons?: boolean;
  images: ImageEntry[];
  showArrowButtons?: boolean;
  favouriteIds?: string[];
  showFileName?: boolean;
  onClickImage?: (imageId: CarouselImage) => void;
  onFavourite?: (imageId: string) => void;
  onCloseModal?: () => void;
  getImageSrc?: (image: ImageEntry) => string;
  estateId?: string;
  projectId?: string;
}

export const ImageCarousel: FC<ImageCarouselProps> = forwardRef<HTMLDivElement, ImageCarouselProps>(
  (
    {
      images,
      imageBeautificationButtons = false,
      preselectedCurrentIndex,
      className,
      imageWrapperClassName,
      imageClassName,
      selectedImageId,
      showArrowButtons = true,
      favouriteIds = [],
      showFileName = false,
      onClickImage,
      onFavourite,
      onCloseModal,
      getImageSrc,
      estateId: contextEstateId,
      projectId: contextProjectId,
    },
    ref
  ) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const location = useLocation();

    const modal = useModal();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { matchesPathes } = useFullPath();

    const queryClient = useQueryClient();

    const nextImage: MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
      setCurrentIndex((prevIndex) => (prevIndex + 1) % (images?.length ?? 1));
    };

    const prevImage: MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
      setCurrentIndex((prevIndex) => (prevIndex === 0 ? (images?.length ?? 1) - 1 : prevIndex - 1));
    };

    const totalImages = images?.length ?? 0;
    const currentImageIsUsedByFlatfinder = images[currentIndex]?.isUsedByFlatfinder === true;

    const { mutateAsync: deleteEstateImage } = useDeleteEstateImage({
      mutationConfig: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getMediaOptions(images[currentIndex]?.estateId ?? '').queryKey,
          });
          toast.success(t('successfullyDeletedImage'));
        },
      },
    });

    const { mutateAsync: deleteProjectImage } = useDeleteProjectImage({
      mutationConfig: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getMediaOptions(images[currentIndex]?.projectId ?? '').queryKey,
          });
          toast.success(t('successfullyDeletedImage'));
        },
      },
    });

    const { mutateAsync: deleteEstateFloorLayouts } = useDeleteFloorLayouts({
      mutationConfig: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getMediaOptions(images[currentIndex]?.estateId ?? '').queryKey,
          });
          toast.success(t('successfullyDeletedImage'));
        },
      },
    });

    const { mutateAsync: deleteProjectFloorLayouts } = useDeleteProjectFloorLayouts({
      mutationConfig: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getMediaOptions(images[currentIndex]?.projectId ?? '').queryKey,
          });
          toast.success(t('successfullyDeletedImage'));
        },
      },
    });

    const shouldShowArrowButtons = useMemo(() => {
      const totalImages = images?.length ?? 0;
      const canShow = showArrowButtons && totalImages > 1;
      return !!canShow;
    }, [images, showArrowButtons]);

    useEffect(() => {
      setCurrentIndex(preselectedCurrentIndex ?? 0);
    }, [preselectedCurrentIndex]);

    useEffect(() => {
      if (!totalImages) {
        setCurrentIndex(0);
        return;
      }
      setCurrentIndex((idx) => Math.min(idx, totalImages - 1));
    }, [totalImages]);

    const handleDeleteImage = async () => {
      try {
        const estateId = images[currentIndex]?.estateId;
        const projectId = images[currentIndex]?.projectId;
        const imageId = images[currentIndex]?.id;

        if (!imageId) return;

        const isEstateFloorPlan =
          estateId && matchesPathes.includes('/estates/$estateId/media/floor-plans');
        const isProjectFloorPlan =
          projectId &&
          (matchesPathes.includes('/estates/$estateId/media/floor-plans') ||
            matchesPathes.includes('/projects/$projectId/media/floor-plans'));

        if (isEstateFloorPlan) {
          await deleteEstateFloorLayouts({ estateId, layoutIds: [imageId] });
          onCloseModal?.();
          return;
        }

        if (estateId) {
          await deleteEstateImage({ estateId, imageIds: [imageId] });
          onCloseModal?.();
          return;
        }

        if (isProjectFloorPlan) {
          await deleteProjectFloorLayouts({ projectId, layoutIds: [imageId] });
          onCloseModal?.();
          return;
        }

        if (projectId) {
          await deleteProjectImage({ projectId, imageIds: [imageId] });
          onCloseModal?.();
        }
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: ignore
        console.error('Failed to delete image:', error);
        toast.error(t('somethingWentWrong'));
      }
    };

    const resolveImageSrc = (image: ImageEntry) => {
      if (getImageSrc) return getImageSrc(image);
      const estateId = image.estateId;
      const isEstateFloorPlan =
        estateId && matchesPathes.includes('/estates/$estateId/media/floor-plans');
      const url = isEstateFloorPlan ? `estates/${estateId}/layouts` : 'images';
      const key = image?.mediaId || image?.id;
      return `${env.API_URL}/${url}/${key}?preview=1`;
    };

    const handleDownloadImage = (image: ImageEntry) => {
      const src = resolveImageSrc(image);

      fetch(src, { credentials: 'include' })
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = ('name' in image ? image.name : 'image') || 'image';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          toast.error(t('somethingWentWrong'));
        });
    };

    const handleCreate3DModel = (_image: ImageCarouselProps['images'][number]) => {
      // TODO: re-direct User to Editor
    };

    const resolveOwnerForNav = (
      img: ImageEntry,
      context?: { estateId?: string; projectId?: string }
    ) => {
      const imageEstateId = img.estateId ?? undefined;
      const fallbackEstateId = context?.estateId ?? undefined;

      const estateId = imageEstateId ?? fallbackEstateId;
      if (estateId) {
        return { estateId, projectId: undefined };
      }

      const projectId = img.projectId ?? context?.projectId ?? undefined;
      return { estateId: undefined, projectId };
    };

    const toCarouselImage = (image: ImageEntry): CarouselImage | null => {
      if (!image.mediaId) return null;

      return {
        id: image.id ?? undefined,
        name: image.name ?? undefined,
        mediaId: image.mediaId,
        estateId: image.estateId ?? undefined,
        projectId: image.projectId ?? undefined,
        sourceId: 'sourceId' in image ? (image.sourceId ?? undefined) : undefined,
        createdAt: image.createdAt ?? undefined,
        updatedAt: image.updatedAt ?? undefined,
        fileType: image.fileType ?? undefined,
      };
    };

    return (
      <>
        <div
          ref={ref}
          className={cn(
            'relative mx-auto flex size-full items-center justify-center overflow-hidden',
            totalImages === 1 && images?.[0]?.fileType === ACCEPTED_MEDIA_TYPES.Pdf
              ? 'min-w-[1200px]'
              : ''
          )}
        >
          <div className="flex h-full flex-col">
            <div className="relative h-full">
              {totalImages === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <p className="text-sm text-muted-foreground">
                    {t('noImagesAvailable') ?? 'No images'}
                  </p>
                </div>
              ) : null}
              <div
                className={cn(
                  'flex size-full items-center justify-center transition-transform duration-300 ease-in-out',
                  className
                )}
                style={{
                  width: `${Math.max(totalImages, 1) * 100}%`,
                  transform:
                    totalImages > 0
                      ? `translateX(-${(100 / totalImages) * currentIndex}%)`
                      : 'translateX(0)',
                }}
              >
                {totalImages > 0 &&
                  images?.map((image, index) => {
                    return (
                      <button
                        key={`${image?.mediaId}-${index}`}
                        type="button"
                        className={cn(
                          'relative flex h-full items-center justify-center',
                          imageWrapperClassName
                            ? typeof imageWrapperClassName === 'string'
                              ? imageWrapperClassName
                              : imageWrapperClassName(selectedImageId === image?.mediaId)
                            : '',
                          onClickImage ? 'cursor-pointer' : ''
                        )}
                        style={{ width: `${100 / (images?.length ?? 1)}%` }}
                        onClick={() => {
                          const carouselImage = toCarouselImage(image);

                          if (!onClickImage || !carouselImage) return;

                          onClickImage(carouselImage);
                        }}
                      >
                        {onFavourite ? (
                          <button
                            type="button"
                            className="absolute right-2 top-2 z-10 cursor-pointer"
                            onClick={() => {
                              if (image?.mediaId) onFavourite(image?.mediaId);
                            }}
                          >
                            <Star
                              className={cn(
                                'text-primary-light',
                                image?.mediaId &&
                                  favouriteIds.includes(image?.mediaId) &&
                                  'fill-primary-light'
                              )}
                            />
                          </button>
                        ) : null}

                        <div
                          className={cn(
                            'flex h-[70vh] items-center justify-center overflow-auto',
                            image?.fileType === ACCEPTED_MEDIA_TYPES.Pdf && totalImages === 1
                              ? 'min-w-[1000px] max-w-[1400px]'
                              : 'w-full'
                          )}
                        >
                          {image?.fileType !== ACCEPTED_MEDIA_TYPES.Pdf ? (
                            <>
                              <img
                                src={resolveImageSrc(image)}
                                alt="carousel"
                                className={cn(
                                  'max-h-full max-w-full object-contain',
                                  imageClassName
                                )}
                              />

                              {showFileName && image.name ? (
                                <p className="absolute bottom-2 left-1/2 w-full -translate-x-1/2 truncate px-4 text-center text-sm text-text-secondary">
                                  {image.name}
                                </p>
                              ) : null}
                            </>
                          ) : (
                            <iframe
                              title={image?.name ?? 'pdf'}
                              src={`${resolveImageSrc(image).replace('?preview=1', '')}#zoom=page-width?preview=1`}
                              className="h-full w-full rounded-md border"
                            />
                          )}
                        </div>
                      </button>
                    );
                  })}
              </div>
              {shouldShowArrowButtons ? (
                <>
                  <button
                    type="button"
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/50 p-2 text-white"
                  >
                    <ChevronLeft className="[&_*]:stroke-primary" />
                  </button>
                  <button
                    type="button"
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/50 p-2 text-white"
                  >
                    <ChevronRight className="[&_*]:stroke-primary" />
                  </button>
                </>
              ) : null}
            </div>
            {imageBeautificationButtons && (
              <div className="flex flex-wrap items-center justify-center gap-1.5 border-t pt-6 xl:gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="w-12 px-4 sm:w-auto"
                      variant="outline"
                      onClick={() => {
                        const img = images[currentIndex];
                        const { estateId, projectId } = resolveOwnerForNav(img, {
                          estateId: contextEstateId,
                          projectId: contextProjectId,
                        });

                        navigate({
                          to: '/image-rendering',
                          search: {
                            imageId: img.mediaId ?? img.id ?? undefined,
                            estateId,
                            projectId,
                            tool: 'ai-rendering',
                          },
                        }).finally(() => onCloseModal?.());
                      }}
                    >
                      <span className="hidden 2xl:block">{t('aiImageRendering')}</span>
                      <span className="block 2xl:hidden">{t('AI')}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="block border bg-white text-black shadow 2xl:hidden">
                    {t('aiImageRendering')}
                  </TooltipContent>
                </Tooltip>

                {!location.pathname.includes('floor-plans') ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-12 gap-2 px-4 sm:w-auto"
                        variant="outline"
                        onClick={() => {
                          const estateId = images[currentIndex]?.estateId;
                          const projectId = images[currentIndex]?.projectId;
                          const mediaId = images[currentIndex]?.mediaId;

                          if (mediaId) {
                            if (estateId)
                              navigate({
                                to: '/estates/$estateId/media/edit/$mediaId',
                                params: {
                                  estateId: estateId,
                                  mediaId: mediaId,
                                },
                              }).finally(() => {
                                if (onCloseModal) {
                                  onCloseModal();
                                }
                              });
                            if (projectId) {
                              navigate({
                                to: '/projects/$projectId/media/edit/$mediaId',
                                params: {
                                  projectId: projectId,
                                  mediaId: mediaId,
                                },
                              }).finally(() => {
                                if (onCloseModal) {
                                  onCloseModal();
                                }
                              });
                            }
                          }
                        }}
                      >
                        <SlidersHorizontal className="size-2" />
                        <span className="hidden 2xl:block">{t('editImage')}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="block border bg-white text-black shadow 2xl:hidden">
                      {t('editImage')}
                    </TooltipContent>
                  </Tooltip>
                ) : parseStringToBool(env.SHOW_PAPER_2_PIXEL) ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-12 gap-2 px-4 sm:w-auto"
                        variant="outline"
                        onClick={() => {
                          const image = images[currentIndex];
                          handleCreate3DModel(image);
                        }}
                      >
                        <span className="hidden 2xl:block">{t('create3DModel')}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="block border bg-white text-black shadow 2xl:hidden">
                      {t('create3DModel')}
                    </TooltipContent>
                  </Tooltip>
                ) : null}

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="w-12 gap-2 px-4 sm:w-auto"
                      variant="outline"
                      onClick={() => {
                        const img = images[currentIndex];
                        const { estateId, projectId } = resolveOwnerForNav(img, {
                          estateId: contextEstateId,
                          projectId: contextProjectId,
                        });

                        navigate({
                          to: '/image-to-video',
                          search: {
                            imageId: img.mediaId ?? img.id ?? undefined,
                            estateId,
                            projectId,
                          },
                        }).finally(() => onCloseModal?.());
                      }}
                    >
                      <Video className="size-4" />
                      <span className="hidden 2xl:block">{t('image-to-video')}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="block border bg-white text-black shadow 2xl:hidden">
                    {t('image-to-video')}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="w-12 gap-2 px-4 sm:w-auto"
                      variant="outline"
                      disabled={currentImageIsUsedByFlatfinder}
                      title={currentImageIsUsedByFlatfinder ? t('imageInUse') : undefined}
                      onClick={modal.open}
                    >
                      <Trash2Icon />
                      <span className="hidden 2xl:block">{t('delete')}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="block border bg-white text-black shadow 2xl:hidden">
                    {t('delete')}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="w-12 gap-2 px-4 sm:w-auto"
                      onClick={() => {
                        const image = images[currentIndex];
                        handleDownloadImage(image);
                      }}
                    >
                      <FileDownIcon />
                      <span className="hidden 2xl:block">{t('download')}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="block border bg-white text-black shadow 2xl:hidden">
                    {t('download')}
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        <ConfirmationModal
          modal={modal}
          title={`${t('delete')} ${t('image')}`}
          description={t('confirmDeleteDocument')}
          confirmationText={t('delete')}
          destructive
          onSubmit={handleDeleteImage}
        />
      </>
    );
  }
);
